import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Download, Spinner, XCircle } from "@phosphor-icons/react";
import { API } from "@/lib/site";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | error
  const [info, setInfo] = useState({});

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) return setState("error");
      if (attempt >= MAX_POLLS) return setState("failed");
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <main className="min-h-screen bg-brand-bg flex items-center justify-center px-6 grain relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-lg w-full rounded-3xl border border-brand-line bg-brand-surface p-10 text-center"
        data-testid="payment-success-page"
      >
        {state === "polling" && (
          <>
            <Spinner size={54} className="animate-spin text-brand-gold mx-auto" />
            <h1 className="font-serif text-4xl text-brand-text mt-6">
              Confirming your payment…
            </h1>
            <p className="text-brand-dim mt-3">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "paid" && info.kind === "booking" && (
          <>
            <CheckCircle size={60} weight="fill" className="text-brand-sage mx-auto" />
            <h1 className="font-serif text-4xl text-brand-text mt-6" data-testid="payment-paid-title">
              Your session is booked.
            </h1>
            <p className="text-brand-dim mt-3">
              {info.booking ? (
                <>
                  <span className="text-brand-text">{info.booking.session_name}</span> on{" "}
                  <span className="text-brand-text">{info.booking.date}</span> at{" "}
                  <span className="text-brand-text">{info.booking.start_time}</span> (
                  {info.booking.tz_label}). A confirmation with your video link is on its way to your inbox.
                </>
              ) : (
                "Payment received. A confirmation email is on its way."
              )}
            </p>
            {info.booking?.meeting_link && (
              <a
                data-testid="booking-meeting-link"
                href={info.booking.meeting_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors mt-8"
              >
                <Download size={18} /> Join the video call
              </a>
            )}
            {info.booking?.gcal_link && (
              <div className="mt-4">
                <a
                  data-testid="gcal-link"
                  href={info.booking.gcal_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-brand-gold hover:text-brand-text transition-colors"
                >
                  + Add to Google Calendar
                </a>
              </div>
            )}
          </>
        )}

        {state === "paid" && info.kind !== "booking" && (
          <>
            <CheckCircle size={60} weight="fill" className="text-brand-sage mx-auto" />
            <h1 className="font-serif text-4xl text-brand-text mt-6" data-testid="payment-paid-title">
              Thank you.
            </h1>
            <p className="text-brand-dim mt-3">
              Your purchase of{" "}
              <span className="text-brand-text">{info.ebook_title || "your order"}</span> is
              complete. Your download{(info.downloads?.length || 0) > 1 ? "s are" : " is"} also on the way to your inbox.
            </p>
            {(info.downloads?.length ? info.downloads : []).length > 0 ? (
              <div className="mt-8 flex flex-col gap-3 items-center">
                {info.downloads.map((dl) => (
                  <a
                    key={dl.key}
                    data-testid={`download-link-${dl.key}`}
                    href={`${API}/ebooks/download/${info.download_token}?key=${dl.key}`}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors w-full justify-center"
                  >
                    <Download size={18} /> {dl.title}
                  </a>
                ))}
              </div>
            ) : (
              info.download_token && (
                <a
                  data-testid="download-link"
                  href={`${API}/ebooks/download/${info.download_token}`}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors mt-8"
                >
                  <Download size={18} /> Download your ebook
                </a>
              )
            )}
          </>
        )}

        {(state === "failed" || state === "error") && (
          <>
            <XCircle size={60} weight="fill" className="text-brand-gold mx-auto" />
            <h1 className="font-serif text-4xl text-brand-text mt-6">
              We couldn't confirm this yet.
            </h1>
            <p className="text-brand-dim mt-3">
              If you were charged, your download link will still arrive by email.
              Otherwise, please try again.
            </p>
          </>
        )}

        <div className="mt-8">
          <Link
            to="/"
            data-testid="back-home"
            className="text-sm text-brand-gold hover:text-brand-text transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </motion.div>
    </main>
  );
}
